import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import {
  BaseAdminScreen,
  AdminEmptyScreenLayout,
  SearchBar,
  InlineEmptyMessage,
  EventDetailsModal,
} from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useEvents } from '@/hooks/admin/useEvents';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { getEventStatusVariant } from '@/utils/admin/events/status';
import { EVENT_VIEW_TYPES, IEvent } from '@/types/admin/events';
import { EventsCalendar } from '@/components/admin/events/EventsCalendar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopEventsView } from './desktop/DesktopEventsView';
import { EventsScreenSkeleton } from './EventsScreenSkeleton';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard, TabSegment, PageHeader } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { SVG_DIMENSIONS } from '@/assets/admin';

type EventsScreenProps = StackScreenProps<AdminStackParamList, 'Events'>;

export const EventsScreen: React.FC<EventsScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { showApiError } = useErrorToast();
  const { redesign, semantic, admin } = useAdminColors();

  const {
    data,
    monthFilteredRows,
    monthlyEvents,
    selectedMonth,
    selectedEvent,
    isLoading,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    view,
    searchQuery,
    updateView,
    updateSearchQuery,
    updateSelectedMonth,
    updateSelectedEvent,
    formatAttendeeLimit,
  } = useEvents();

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  const renderViewTabs = () => (
    <View style={styles.viewTabsWrapper}>
      <TabSegment
        items={[
          { key: EVENT_VIEW_TYPES.CALENDAR, label: 'Calendar' },
          { key: EVENT_VIEW_TYPES.LIST, label: 'List' },
        ]}
        selectedKey={view}
        onChange={(key) => updateView(key as typeof view)}
        variant="pill"
      />
    </View>
  );

  const renderCalendarView = () => {
    return (
      <EventsCalendar
        monthlyEvents={monthlyEvents}
        selectedMonth={selectedMonth}
        onMonthChange={updateSelectedMonth}
        onEditEvent={handleEditEvent}
      />
    );
  };

  const renderListView = () => {
    const hasFilteredData = monthFilteredRows.length > 0;
    if (hasFilteredData) {
      return (
        <FlatList
          data={monthFilteredRows}
          keyExtractor={(item) => item.id.toString()}
          showsVerticalScrollIndicator={false}
          style={styles.listContainer}
          renderItem={({ item: event }) => (
            <TouchableOpacity
              onPress={() => handleListEventPress(event.id)}
              activeOpacity={0.7}
            >
              <GlassCard padding={16} borderRadius={8} style={styles.eventCardSpacing}>
                <View style={styles.eventCardHeader}>
                  <Text style={[styles.eventName, { color: redesign.text.primary }]}>
                    {event.name}
                  </Text>
                  <GlassStatusBadge
                    label={event.status}
                    variant={getEventStatusVariant(event.status)}
                  />
                </View>
                <View style={styles.eventCardBody}>
                  <Text style={[styles.eventLocation, { color: redesign.text.muted }]}>
                    {event.location}
                  </Text>
                  <Text style={[styles.eventTime, { color: redesign.text.muted }]}>
                    {new Date(event.startTime).toLocaleDateString()} •{' '}
                    {new Date(event.startTime).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                  <Text style={[styles.eventRsvps, { color: redesign.text.subtle }]}>
                    {event.n_rsvps} RSVPs • {formatAttendeeLimit(event.rsvpLimit)} max
                  </Text>
                </View>
              </GlassCard>
            </TouchableOpacity>
          )}
        />
      );
    }

    // Show contextual empty message for search results
    if (hasSearchQuery) {
      return (
        <InlineEmptyMessage
          title={EMPTY_MESSAGES.events.noResults(searchQuery).title}
          message={EMPTY_MESSAGES.events.noResults(searchQuery).message}
          icon={EMPTY_MESSAGES.events.noResults(searchQuery).icon}
          containerStyle={styles.noResultsContainer}
          titleStyle={{ ...styles.noResultsTitle, color: redesign.text.primary }}
          messageStyle={{ ...styles.noResultsMessage, color: redesign.text.muted }}
        />
      );
    }

    // Show default empty state when no search is active
    return renderEmptyMessage();
  };

  const renderEmptyMessage = () => {
    const cfg = EMPTY_MESSAGES.events.default;
    return (
      <InlineEmptyMessage
        title={cfg.title}
        message={cfg.message}
        icon={cfg.icon}
        containerStyle={styles.emptyMessageContainer}
        titleStyle={{ ...styles.emptyMessageText, color: redesign.text.primary }}
        messageStyle={{ ...styles.emptyMessageSubtext, color: redesign.text.muted }}
      />
    );
  };

  // Handle create event functionality
  const handleAddEvent = useCallback(() => {
    navigation.navigate('CreateEvent');
  }, [navigation]);

  // Determine if we should show data or empty state
  const hasData = data?.data && data.data.length > 0;
  const hasSearchQuery = searchQuery.trim() !== '';

  const headerSubtitle = useMemo(() => {
    const monthLabel = selectedMonth.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    const count = monthFilteredRows.length;
    return `${count} event${count === 1 ? '' : 's'} · ${monthLabel}`;
  }, [monthFilteredRows.length, selectedMonth]);

  const headerAddButton = useMemo(() => (
    <TouchableOpacity
      style={styles.headerAddButton}
      onPress={handleAddEvent}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel="Create event"
    >
      <SVG_COMPONENTS.ADD_BUTTON {...SVG_DIMENSIONS.ADD_BUTTON} />
    </TouchableOpacity>
  ), [handleAddEvent]);

  // Modal state for list view selections
  const [modalVisible, setModalVisible] = useState(false);

  const handleListEventPress = useCallback(
    (eventId: number) => {
      const fullEvent = (data?.data || []).find((e: IEvent) => e.id === eventId);
      if (fullEvent) {
        updateSelectedEvent(fullEvent);
        setModalVisible(true);
      }
    },
    [data, updateSelectedEvent],
  );

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    updateSelectedEvent(null);
  }, [updateSelectedEvent]);

  const handleEditEvent = useCallback(
    (eventId: number) => {
      setModalVisible(false);
      updateSelectedEvent(null);
      navigation.navigate('EditEvent', { eventId });
    },
    [navigation, updateSelectedEvent],
  );

  // Show error state if we have a critical error and no data and no active search
  const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchQuery;

  // Show loading state if we're loading and have no data yet and no active search
  const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchQuery;

  // Render error state component
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text style={[styles.errorTitle, { color: redesign.text.primary }]}>
        Unable to Load Events
      </Text>
      <Text style={[styles.errorMessage, { color: redesign.text.muted }]}>
        {enhancedError?.userFriendlyMessage ||
          'Something went wrong while loading the events.'}
      </Text>

      {retryCount > 0 && (
        <Text style={[styles.retryInfo, { color: redesign.text.subtle }]}>
          Retry attempts: {retryCount}
        </Text>
      )}

      {canRetry && (
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: admin.formInput.borderFocused }]}
          onPress={manualRetry}
        >
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // Render content based on view mode
  const renderContent = () => {
    if (showLoadingState) {
      return <EventsScreenSkeleton />;
    }

    if (showErrorState) {
      return renderErrorState();
    }

    return (
      <View style={styles.contentContainer}>
        {renderViewTabs()}

        {/* Search Bar - Only show for List view */}
        {view === EVENT_VIEW_TYPES.LIST && (
          <View style={styles.searchContainer}>
            <SearchBar
              placeholder="Search events..."
              value={searchQuery}
              onChangeText={updateSearchQuery}
            />
          </View>
        )}

        {view === EVENT_VIEW_TYPES.CALENDAR ? renderCalendarView() : renderListView()}
      </View>
    );
  };

  // Desktop web rendering
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="EventsScreenDesktop">
        <DesktopEventsView
          monthlyEvents={monthlyEvents}
          selectedMonth={selectedMonth}
          selectedEvent={selectedEvent as IEvent | null}
          searchQuery={searchQuery}
          onSearchChange={updateSearchQuery}
          onMonthChange={updateSelectedMonth}
          onSelectEvent={(e) => updateSelectedEvent(e)}
          onCreateEvent={handleAddEvent}
          onEditEvent={handleEditEvent}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="EventsScreen">
      <DataTableProvider
        tableKey="admin:events"
        defaults={{ paginationModel: { page: 0, pageSize: 25 } }}
      >
        <BaseAdminScreen
          screenName="Events"
          navigation={navigation}
          showPlaceholder={false}
          scrollEnabled={false}
          contentContainerStyle={styles.baseScreenContent}
        >
          <AdminEmptyScreenLayout
            title="Events"
            emptyStateType="events"
            onAddPress={handleAddEvent}
            showAddButton={true}
            buttonType="add"
            hasContent={true}
            disableScrollView={true}
            showTitleBar={false}
          >
            <PageHeader
              title="Events"
              subtitle={headerSubtitle}
              rightAction={headerAddButton}
            />
            {renderContent()}
          </AdminEmptyScreenLayout>

          {/* Event Details Modal (for List view selection) */}
          <AdminErrorBoundary context="EventDetailsModal">
            <EventDetailsModal
              visible={modalVisible}
              event={selectedEvent as IEvent | null}
              onClose={handleCloseModal}
              onEdit={handleEditEvent}
            />
          </AdminErrorBoundary>
        </BaseAdminScreen>
      </DataTableProvider>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  headerAddButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    flex: 1,
  },
  viewTabsWrapper: {
    marginBottom: 16,
  },
  searchContainer: {
    marginBottom: 16,
  },

  // List view styles
  listContainer: {
    flex: 1,
  },
  eventCardSpacing: {
    marginBottom: 12,
  },
  eventCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  eventName: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    marginRight: 12,
  },
  eventCardBody: {
    gap: 4,
  },
  eventLocation: {
    fontSize: 14,
  },
  eventTime: {
    fontSize: 14,
  },
  eventRsvps: {
    fontSize: 14,
  },

  // Error state styles
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Empty message styles
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -120,
    paddingHorizontal: 24,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -120,
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});
